import React, { useState, useEffect } from 'react';
import {
  Activity, History, FolderOpen, BarChart3, Settings,
  Shield, LogOut, Menu, X as CloseIcon, Copy, Eye, GitCompare,
  Sun, Moon, Sparkles, List as DetailsIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDemo } from '../context/DemoContext';
import { useTheme } from '../context/ThemeContext';
import { analyzeService, settingsService } from '../services';
import { adaptAnalysis } from '../models/analysis';
import { toast } from 'sonner';
import useAnalyzeDraftStore from '../store/analyzeDraftStore';

import { APP, NAV } from '../design-system/content';
import { DemoBadge } from '../components/DemoBadge';
import { OnboardingOverlay } from '../components/OnboardingOverlay';

import { InputPanel } from '../components/workspace/InputPanel';
import { ScoreCard } from '../components/workspace/ScoreCard';
import { DimensionCards } from '../components/workspace/DimensionCards';
import { DriversPanel } from '../components/workspace/DriversPanel';
import { EvidencePanel } from '../components/workspace/EvidencePanel';
import { OptimizationPanel } from '../components/workspace/OptimizationPanel';
import { OptimizeProgressPanel } from '../components/workspace/OptimizeProgressPanel';
import { JudgeStatus } from '../components/workspace/JudgeStatus';
import { ComparisonPanel } from '../components/workspace/ComparisonPanel';
import { EmptyState, ErrorState, LoadingSkeleton } from '../components/workspace/States';
import { GlassPageHeader } from '../components/ui/glass/GlassPageHeader';
import { GlassPill, GlassSegmentedControl } from '../components/ui/glass/GlassCard';
import { useCursorParallax } from '../hooks/useCursorParallax';

/**
 * WorkspacePage (Analyze) — the flagship 3-zone liquid-glass workspace.
 *
 * Zones:
 *   Left (input)   : tactile glass input surface, persistent save chip,
 *                    luminous Analyze CTA.
 *   Center (score) : hero HQ-score card with radial glow, six-dimension
 *                    glass metric modules, drivers, evidence.
 *   Right (verdict): glass segmented Details/Compare control, optimized
 *                    rewrite glass document, judge verdict status card.
 */
export default function WorkspacePage() {
  const { user, logout } = useAuth();
  const { demoMode } = useDemo();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  // Ambient cursor-driven shell parallax
  useCursorParallax({ strength: 14 });

  // Persistent draft state
  const aiOutput          = useAnalyzeDraftStore(s => s.aiOutput);
  const originalPrompt    = useAnalyzeDraftStore(s => s.originalPrompt);
  const draftDomain       = useAnalyzeDraftStore(s => s.domain);
  const draftAudience     = useAnalyzeDraftStore(s => s.audience);
  const draftSensitivity  = useAnalyzeDraftStore(s => s.sensitivity);
  const setAiOutput       = useAnalyzeDraftStore(s => s.setAiOutput);
  const setOriginalPrompt = useAnalyzeDraftStore(s => s.setOriginalPrompt);
  const setDraftDomain    = useAnalyzeDraftStore(s => s.setDomain);
  const setDraftAudience  = useAnalyzeDraftStore(s => s.setAudience);
  const setDraftSensitivity = useAnalyzeDraftStore(s => s.setSensitivity);
  const clearDraft        = useAnalyzeDraftStore(s => s.clearAnalyzeDraft);

  const [sessionMode, setSessionMode] = useState('ephemeral');

  const context = {
    userPrompt: originalPrompt,
    domain: draftDomain,
    audience: draftAudience,
    sensitivity: draftSensitivity,
  };
  const setContext = (updater) => {
    const next = typeof updater === 'function' ? updater(context) : updater;
    if (next.userPrompt !== undefined && next.userPrompt !== originalPrompt) setOriginalPrompt(next.userPrompt);
    if (next.domain !== undefined && next.domain !== draftDomain) setDraftDomain(next.domain);
    if (next.audience !== undefined && next.audience !== draftAudience) setDraftAudience(next.audience);
    if (next.sensitivity !== undefined && next.sensitivity !== draftSensitivity) setDraftSensitivity(next.sensitivity);
  };

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [judging, setJudging] = useState(false);
  const [error, setError] = useState('');

  const [activeDimension, setActiveDimension] = useState(null);
  const [compareMode, setCompareMode] = useState(false);

  useEffect(() => {
    settingsService.get().then(s => {
      setSessionMode(s.storage_mode || 'ephemeral');
    }).catch(() => {});
  }, []);

  const handleAnalyze = async () => {
    if (!aiOutput.trim()) return;
    setError(''); setLoading(true); setAnalysis(null); setActiveDimension(null);
    try {
      const raw = await analyzeService.score(aiOutput, sessionMode, context);
      setAnalysis(adaptAnalysis(raw));
    } catch (err) {
      setError(err.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally { setLoading(false); }
  };

  const [optimizeProgress, setOptimizeProgress] = useState(null);
  const [optimizeError, setOptimizeError] = useState(null);

  const handleOptimize = async () => {
    if (!analysis?.id || optimizing) return;
    setOptimizing(true);
    setOptimizeProgress({ stage: 0, percent: 0, title: 'Starting...', desc: '' });
    setOptimizeError(null); setError('');
    try {
      const raw = await analyzeService.optimizeStream(analysis.id, (p) => setOptimizeProgress(p));
      const adapted = adaptAnalysis(raw);
      if (raw.judge_verdict) {
        adapted.judge = {
          verdict: raw.judge_verdict.overall_verdict,
          approved: raw.judge_verdict.approved,
          intentStatus: raw.judge_verdict.intent_status,
          intentSimilarity: raw.judge_verdict.intent_similarity,
          safetyStatus: raw.judge_verdict.safety_status,
          dependencyIncrease: raw.judge_verdict.dependency_increase,
          notes: raw.judge_verdict.notes,
        };
      }
      if (raw.blocked) toast.error(raw.block_reason || 'Rewrite blocked by judge');
      // Surface intent-protection fallback (rewrite was reverted to original)
      // as a friendly notice rather than letting the user wonder why the
      // "after" text is identical to the input.
      if (raw.returned_original || raw.fallback_reason === 'intent_protection') {
        toast.warning(
          raw.optimization_notes ||
          'We could not safely improve this without changing your meaning, so we kept the original.',
          { duration: 6000 }
        );
      }
      setAnalysis(adapted);
    } catch (err) {
      // The optimizeStream service attaches userTitle/userMessage on
      // friendly errors (e.g. AI quota exhausted, rate-limited).
      const title = err.userTitle || 'Optimization failed';
      const msg = err.userMessage || err.message || 'Optimization failed.';
      setOptimizeError(msg);
      toast.error(title, { description: msg, duration: 7000 });
    } finally {
      setOptimizing(false);
      setTimeout(() => setOptimizeProgress(null), 1200);
    }
  };

  const handleJudge = async () => {
    if (!analysis?.id) return;
    setJudging(true);
    try {
      const raw = await analyzeService.judgeIntent(analysis.id);
      const jv = raw.judge_verdict || raw;
      setAnalysis(prev => ({
        ...prev,
        judge: {
          verdict: jv.overall_verdict,
          approved: jv.approved,
          intentStatus: jv.intent_status,
          intentSimilarity: jv.intent_similarity,
          safetyStatus: jv.safety_status,
          dependencyIncrease: jv.dependency_increase,
          notes: jv.notes,
        },
      }));
      toast.success('Judge verification complete');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.error || 'Judge verification failed.');
    } finally { setJudging(false); }
  };

  const handleClear = () => { clearDraft(); setAnalysis(null); setError(''); setActiveDimension(null); };
  const handleCopyOriginal = () => {
    if (aiOutput) { navigator.clipboard.writeText(aiOutput); toast.success('Original text copied'); }
  };

  const saveChipVariant = sessionMode === 'persisted' ? 'success' : 'default';
  const saveChipLabel   = sessionMode === 'persisted' ? 'Persisting runs' : 'Ephemeral mode';

  return (
    <div className="liquid-shell min-h-screen flex flex-col lg:h-screen lg:overflow-hidden" data-testid="workspace-page">
      <a href="#workspace-main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-[hsl(var(--accent-cyan))] focus:text-white focus:rounded-md">
        Skip to content
      </a>

      {/* Mobile header */}
      <header className="lg:hidden msml-glass sticky top-0 z-40" role="banner">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[linear-gradient(135deg,hsl(var(--accent-cyan)),hsl(var(--accent-teal)))]">
              <Activity className="w-4 h-4 text-white" />
            </span>
            <span className="font-semibold text-sm text-ink">{APP.name}</span>
            <DemoBadge />
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-ink-2 hover:text-ink" data-testid="mobile-menu-btn" aria-label="Toggle navigation menu" aria-expanded={mobileMenuOpen}>
            {mobileMenuOpen ? <CloseIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="border-t border-[var(--glass-stroke)] bg-[var(--glass-fill-panel)] backdrop-blur-xl p-3 space-y-1 msml-fade-in">
            <SideNavItem to="/workspace" icon={<Activity />}   label={NAV.workspace} active />
            <SideNavItem to="/history"   icon={<History />}    label={NAV.history} />
            <SideNavItem to="/cases"     icon={<FolderOpen />} label={NAV.cases} />
            <SideNavItem to="/reports"   icon={<BarChart3 />}  label={NAV.reports} />
            {demoMode && <SideNavItem to="/demo" icon={<Eye />} label="Demo" />}
            <SideNavItem to="/settings"  icon={<Settings />}   label={NAV.settings} />
            {user?.role === 'admin' && <SideNavItem to="/admin" icon={<Shield />} label={NAV.admin} />}
            <button onClick={toggleTheme} className="liquid-nav-item w-full" data-testid="mobile-theme-toggle-workspace">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
            <button onClick={() => { logout(); navigate('/'); }} className="liquid-nav-item w-full text-left" data-testid="mobile-logout-btn">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </nav>
        )}
      </header>

      {/* Main layout */}
      <div className="flex-1 lg:grid lg:grid-cols-[248px_1fr] lg:overflow-hidden">
        {/* Desktop glass sidebar */}
        <aside className="hidden lg:flex flex-col liquid-sidebar h-full" role="navigation" aria-label="Main navigation" data-testid="workspace-sidebar">
          <Link to="/workspace" className="flex items-center gap-2.5 px-5 h-16 border-b border-[var(--glass-stroke)]">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center bg-[linear-gradient(135deg,hsl(var(--accent-cyan)),hsl(var(--accent-teal)))] shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_0_16px_hsl(var(--accent-cyan)/0.35)]" aria-hidden>
              <Activity className="w-4 h-4 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-ink tracking-tight text-[0.95rem]">{APP.name}</span>
                <DemoBadge />
              </div>
              <span className="block text-[10px] font-mono uppercase tracking-[0.22em] text-ink-muted leading-none mt-0.5">HQ · Evaluation</span>
            </div>
          </Link>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin">
            <SideNavItem to="/workspace" icon={<Activity />}   label={NAV.workspace} active />
            <SideNavItem to="/history"   icon={<History />}    label={NAV.history} />
            <SideNavItem to="/cases"     icon={<FolderOpen />} label={NAV.cases} />
            <SideNavItem to="/reports"   icon={<BarChart3 />}  label={NAV.reports} />
            {demoMode && <SideNavItem to="/demo" icon={<Eye />} label="Demo" />}
            <SideNavItem to="/settings"  icon={<Settings />}   label={NAV.settings} />
            {user?.role === 'admin' && <SideNavItem to="/admin" icon={<Shield />} label={NAV.admin} />}
          </nav>
          <div className="p-3 border-t border-[var(--glass-stroke)] space-y-2">
            <button onClick={toggleTheme} className="liquid-nav-item w-full" data-testid="theme-toggle-btn" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
            <div className="liquid-card p-2.5 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[linear-gradient(135deg,hsl(var(--accent-cyan)/0.3),hsl(var(--accent-violet)/0.3))] text-ink font-medium text-sm border border-[var(--glass-stroke-strong)]">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-ink truncate">{user?.name}</p>
                <p className="text-[10px] text-ink-muted truncate">{user?.email}</p>
              </div>
              <button onClick={() => { logout(); navigate('/'); }} className="p-1.5 rounded-md text-ink-3 hover:text-ink transition-colors" data-testid="sidebar-logout-btn" title="Sign out" aria-label="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Content area */}
        <div className="flex-1 lg:overflow-hidden flex flex-col" id="workspace-main" role="main">
          {/* Floating page header */}
          <div className="px-4 pt-4 lg:px-6 lg:pt-6">
            <GlassPageHeader
              eyebrow="Analysis workspace"
              title="Analyze"
              subtitle="Score AI output against the 6-dimension rubric, then generate an intent-preserving rewrite verified by an independent judge."
              icon={<Sparkles />}
              actions={
                <GlassPill variant={saveChipVariant} live={sessionMode === 'persisted'} data-testid="workspace-save-state-chip">
                  {saveChipLabel}
                </GlassPill>
              }
            />
          </div>

          {/* 3-zone grid */}
          <div className="flex-1 lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[minmax(300px,1fr)_minmax(320px,1.15fr)_minmax(300px,0.95fr)] gap-4 lg:gap-5 px-4 pt-4 pb-6 lg:px-6 lg:pt-5 lg:pb-6">
            {/* Column 1: Input */}
            <div className="lg:overflow-y-auto lg:pr-1 scrollbar-thin space-y-4" data-testid="input-column">
              <InputPanel
                text={aiOutput} setText={setAiOutput}
                sessionMode={sessionMode} setSessionMode={setSessionMode}
                context={context} setContext={setContext}
                onAnalyze={handleAnalyze} loading={loading}
                hasResult={!!analysis} onClear={handleClear}
              />
              {aiOutput && analysis && (
                <div className="flex justify-end">
                  <button onClick={handleCopyOriginal} className="liquid-pill hover:text-ink" data-testid="copy-original-btn">
                    <Copy className="w-3 h-3" /> Copy original
                  </button>
                </div>
              )}
            </div>

            {/* Column 2: Score */}
            <div className="lg:overflow-y-auto lg:px-0.5 scrollbar-thin space-y-4" data-testid="score-column">
              {error && <ErrorState message={error} onDismiss={() => setError('')} />}
              {loading && <LoadingSkeleton />}
              {!loading && !analysis && !error && <EmptyState />}
              {!loading && analysis && (
                <>
                  <ScoreCard analysis={analysis} />
                  <DimensionCards
                    dimensions={analysis.dimensions}
                    activeDimension={activeDimension}
                    onDimensionClick={key => setActiveDimension(prev => prev === key ? null : key)}
                  />
                  <DriversPanel drivers={analysis.drivers} activeDimension={activeDimension} onDriverClick={setActiveDimension} />
                  <EvidencePanel evidence={analysis.evidence} activeDimension={activeDimension} />
                </>
              )}
            </div>

            {/* Column 3: Verdict */}
            <div className="lg:overflow-y-auto lg:pl-1 scrollbar-thin space-y-3" data-testid="optimization-column">
              {analysis && (
                <>
                  {analysis.optimization && (
                    <GlassSegmentedControl
                      testIdPrefix="view"
                      value={compareMode ? 'compare' : 'details'}
                      onChange={(v) => setCompareMode(v === 'compare')}
                      items={[
                        { value: 'details', label: 'Details', icon: <DetailsIcon /> },
                        { value: 'compare', label: 'Compare', icon: <GitCompare /> },
                      ]}
                      className="self-start"
                    />
                  )}

                  {compareMode && analysis.optimization ? (
                    <ComparisonPanel analysis={analysis} originalText={aiOutput} />
                  ) : (
                    <>
                      {optimizing && optimizeProgress && (
                        <OptimizeProgressPanel progress={optimizeProgress} error={optimizeError} />
                      )}
                      <OptimizationPanel analysis={analysis} onOptimize={handleOptimize} optimizing={optimizing} />
                      <JudgeStatus analysis={analysis} onJudge={handleJudge} judging={judging} />
                    </>
                  )}
                </>
              )}
              {!analysis && (
                <div className="hidden lg:flex h-full items-center justify-center">
                  <p className="text-xs text-ink-muted text-center max-w-[220px] leading-relaxed">
                    Run an analysis to see optimization and judge verdict controls.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden msml-glass border-t border-[var(--glass-stroke)] sticky bottom-0 z-40" aria-label="Mobile navigation">
        <div className="flex items-center justify-around py-2">
          <MobileNavItem to="/workspace" icon={<Activity />}   label={NAV.workspace} active />
          <MobileNavItem to="/history"   icon={<History />}    label={NAV.history} />
          {demoMode
            ? <MobileNavItem to="/demo"  icon={<Eye />}        label="Demo" />
            : <MobileNavItem to="/cases" icon={<FolderOpen />} label={NAV.cases} />}
          <MobileNavItem to="/reports"   icon={<BarChart3 />}  label={NAV.reports} />
          <MobileNavItem to="/settings"  icon={<Settings />}   label={NAV.settings} />
        </div>
      </nav>

      <OnboardingOverlay />
    </div>
  );
}

function SideNavItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className="liquid-nav-item"
      data-active={active ? 'true' : 'false'}
      data-testid={`nav-${label.toLowerCase()}`}
    >
      {React.cloneElement(icon, { className: 'w-4 h-4 shrink-0' })}
      <span className="truncate">{label}</span>
    </Link>
  );
}

function MobileNavItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center gap-1 px-3 py-1 text-[11px] font-medium transition-colors ${active ? 'text-[hsl(var(--accent-cyan))]' : 'text-ink-3'}`}
      data-testid={`mobile-nav-${label.toLowerCase()}`}
    >
      {React.cloneElement(icon, { className: 'w-5 h-5' })}
      {label}
    </Link>
  );
}
